import type { PaginationState, SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { AuditTrailDrawer } from '@/components/audit-trail-drawer'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { ErrorSurface, ProblemAlert } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatMoney, type Pesewas } from '@/lib/money'
import { ApiError } from '@/lib/problem-details'

/**
 * A development-only preview of the shared primitives.
 *
 * The route is mounted only when `import.meta.env.DEV` is true, so it cannot reach a production
 * build. The rows below are obviously synthetic and exist to exercise alignment, empty states, and
 * keyboard behaviour — no screen that an operator sees ever renders invented figures.
 */

interface PreviewRow {
  id: string
  accountNumber: string
  customer: string
  outstanding: Pesewas
  arrears: Pesewas
  status: string
}

const PREVIEW_ROWS: PreviewRow[] = [
  {
    id: '1',
    accountNumber: '001200005134',
    customer: 'Ama Serwaa',
    outstanding: 116_000,
    arrears: 0,
    status: 'Active',
  },
  {
    id: '2',
    accountNumber: '001200006622',
    customer: 'Kwabena Mensah',
    outstanding: 348_050,
    arrears: 7_250,
    status: 'Overdue',
  },
  {
    id: '3',
    accountNumber: '001200007415',
    customer: 'Yaa Boateng',
    outstanding: 0,
    arrears: 0,
    status: 'FullyRepaid',
  },
  {
    id: '4',
    accountNumber: '001200008109',
    customer: 'Kofi Asare',
    outstanding: 1_240_000,
    arrears: 96_500,
    status: 'PendingApproval',
  },
]

const SAMPLE_ERROR = new ApiError({
  status: 422,
  code: 'loans.security_deposit_insufficient',
  title: 'That operation is not currently possible',
  detail: 'Security deposit balance 120.00 is below the required 250.00.',
  correlationId: '018f3f7c-6a1e-7b5a-9d2c-2f0f5b7c4a11',
})

const OFFLINE_ERROR = new ApiError({
  status: 0,
  code: 'network.unreachable',
  title: 'No connection to the server',
  isNetworkFailure: true,
})

const helper = dataTableColumns<PreviewRow>()

const columns: DataTableColumn<PreviewRow>[] = helper.columns([
  helper.accessor('accountNumber', {
    header: 'Account',
    cell: (context) => <span className="font-mono text-xs">{context.row.original.accountNumber}</span>,
    meta: { noWrap: true },
  }),
  helper.accessor('customer', { header: 'Customer' }),
  helper.accessor('outstanding', {
    header: 'Outstanding',
    cell: (context) => <MoneyDisplay value={context.row.original.outstanding} />,
    meta: { align: 'right', widthClass: 'w-40' },
  }),
  helper.accessor('arrears', {
    header: 'In arrears',
    cell: (context) => (
      <MoneyDisplay value={context.row.original.arrears} colourNegative emphasis="strong" />
    ),
    meta: { align: 'right', widthClass: 'w-40' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-36' },
  }),
])

export function PrimitivesPreviewPage() {
  const [amount, setAmount] = useState<Pesewas | null>(125_050)
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 })
  const [sorting, setSorting] = useState<SortingState>([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [auditOpen, setAuditOpen] = useState(false)

  const total = useMemo(
    () => PREVIEW_ROWS.reduce((running, row) => running + row.outstanding, 0),
    [],
  )

  return (
    <>
      <PageHeader
        title="Component preview"
        description="Development build only. Static sample rows, used to check alignment, empty states, and keyboard behaviour."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Money</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <MoneyInput value={amount} onChange={setAmount} aria-label="Amount" />
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-muted">Stored value</dt>
              <dd className="numeric text-right font-mono tabular-nums">
                {amount === null ? 'null' : `${String(amount)} pesewas`}
              </dd>
              <dt className="text-muted">Formatted</dt>
              <dd>
                <MoneyDisplay value={amount} />
              </dd>
              <dt className="text-muted">Accounting negative</dt>
              <dd>
                <MoneyDisplay value={amount === null ? null : -amount} negativeStyle="parentheses" />
              </dd>
              <dt className="text-muted">Portfolio total</dt>
              <dd>
                <MoneyDisplay value={total} emphasis="strong" />
              </dd>
            </dl>
            <p className="text-2xs text-muted">
              {formatMoney(100, { symbol: false })} is one cedi. Values never leave integer pesewas.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Errors and confirmation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ProblemAlert error={SAMPLE_ERROR} />
            <div className="flex gap-2">
              <Button variant="danger" onClick={() => setConfirmOpen(true)}>
                Reverse transaction
              </Button>
              <Button variant="secondary" onClick={() => setAuditOpen(true)}>
                Audit trail
              </Button>
            </div>
            <ErrorSurface error={OFFLINE_ERROR} onRetry={() => undefined} />
          </CardContent>
        </Card>
      </div>

      <DataTable
        columns={columns}
        data={PREVIEW_ROWS}
        rowCount={PREVIEW_ROWS.length}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        getRowId={(row) => row.id}
        onRowActivate={(row) => window.alert(`Activated ${row.accountNumber}`)}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        tone="danger"
        title="Reverse this transaction?"
        description="A reversal posts a contra entry into the current business day. The original posting stays in the ledger."
        confirmationPhrase="REVERSE"
        confirmLabel="Reverse"
        onConfirm={() => setConfirmOpen(false)}
      />

      <AuditTrailDrawer
        open={auditOpen}
        onOpenChange={setAuditOpen}
        entityType="LoanCycle"
        entityId="018f3f7c-6a1e-7b5a-9d2c-2f0f5b7c4a11"
        subject="Preview record"
      />
    </>
  )
}
